import { Outlet, Link, useLocation } from 'react-router-dom'
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { NotificationCenter } from "@/components/ui/notification-center"
import VerificationPrompt from "@/components/VerificationPrompt"
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/lib/api/client'
import { tenantGetCertification as sdkGetCertification } from '@/lib/api/generated/sdk.gen'
import { useAuth } from '@/hooks/useAuth'
import { useState } from 'react'
import { cn } from "@/lib/utils"
import { 
  Home,
  Send, 
  FileText, 
  CreditCard,
  BarChart3,
  LogOut,
  Menu,
  User,
  HelpCircle,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  Reply,
  History,
  Plus,
  PenTool,
  Key,
  Webhook
} from 'lucide-react'

// 菜单项配置
const menuItems = [
  {
    title: '工作台',
    href: '/dashboard',
    icon: Home,
    description: '数据概览、快捷操作'
  },
  {
    title: '短信管理',
    icon: Send,
    description: '短信发送和记录管理',
    children: [
      {
        title: '发送短信',
        href: '/dashboard/send',
        description: '在线发送、批量发送',
        icon: Plus
      },
      {
        title: '发送记录',
        href: '/dashboard/records',
        description: '历史记录、状态查询',
        icon: History
      },
      {
        title: '上行消息',
        href: '/dashboard/mo-messages',
        description: '用户回复消息管理',
        icon: Reply
      }
    ]
  },
  {
    title: '内容管理',
    icon: FileText,
    description: '模板和签名管理',
    children: [
      {
        title: '模板管理',
        href: '/dashboard/templates',
        description: '我的模板、申请模板',
        icon: FileText
      },
      {
        title: '签名管理',
        href: '/dashboard/signatures',
        description: '我的签名、申请签名',
        icon: PenTool
      }
    ]
  },
  {
    title: '账户中心',
    icon: CreditCard,
    description: '账户与资金相关功能',
    children: [
      { title: '概览', href: '/dashboard/account', description: '账户信息、余额管理' },
      { title: '充值', href: '/dashboard/account/recharge', description: '账户充值' },
      { title: '费用明细', href: '/dashboard/account/transactions', description: '交易记录与明细' },
      { title: '用量统计', href: '/dashboard/account/usage', description: '短信用量与趋势' }
    ]
  },
  {
    title: '凭据与集成',
    icon: User,
    description: 'API凭据与回调集成',
    children: [
      { title: 'API Keys', href: '/dashboard/credentials/api-keys', description: '管理API密钥', icon: Key },
      { title: 'Webhooks', href: '/dashboard/credentials/webhooks', description: '回调与事件订阅', icon: Webhook }
    ]
  },
  {
    title: '设置',
    icon: HelpCircle,
    description: '账户与安全设置',
    children: [
      { title: '账户设置', href: '/dashboard/settings/account', description: '基本资料与资质认证' },
      { title: '安全设置', href: '/dashboard/settings/security', description: '密码与双因素认证' },
      { title: '组织与权限', href: '/dashboard/settings/organization', description: '成员与角色' },
      { title: '通知设置', href: '/dashboard/settings/notifications', description: '消息通知与偏好' }
    ]
  },
  {
    title: '数据报表',
    href: '/dashboard/reports',
    icon: BarChart3,
    description: '发送报表、成本分析'
  }
]

export default function DashboardLayout() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['短信管理', '内容管理', '账户中心', '设置'])

  // 查询企业认证状态用于展示侧边栏状态徽标
  const { data: certData } = useQuery({
    queryKey: ['tenant','certification','sidebar'],
    queryFn: async () => {
      const res: any = await sdkGetCertification({ client: apiClient, responseStyle: 'fields', throwOnError: false })
      const payload: any = (res as any)?.data ?? res
      return payload?.data ?? null
    },
  })
  const certStatus: 'NOT_SUBMITTED'|'PENDING'|'VERIFIED'|'REJECTED'|undefined = certData?.certificationStatus

  return (
    <div className="flex h-screen bg-gray-50">
      {/* 侧边栏 */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'} fixed inset-0 z-50 lg:relative lg:block lg:w-64 lg:flex-shrink-0`}>
        <div className="flex h-full w-64 flex-col bg-white shadow-lg">
          {/* Logo区域 */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <Link to="/dashboard" className="flex items-center space-x-2">
              <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">云信SMS</h1>
                <p className="text-xs text-gray-600">企业用户</p>
              </div>
            </Link>
          </div>
          
          {/* 导航菜单 */}
          <nav className="flex-1 mt-6 px-3 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              if (item.children) {
                // 二级菜单
                const isExpanded = expandedMenus.includes(item.title)
                const hasActiveChild = item.children.some(child => 
                  location.pathname === child.href || 
                  (child.href !== '/dashboard' && location.pathname.startsWith(child.href))
                )
                
                return (
                  <div key={item.title}>
                    {/* 父级菜单 */}
                    <button
                      className={cn(
                        "w-full group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors",
                        hasActiveChild
                          ? "bg-blue-50 text-blue-700"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      )}
                      onClick={() => {
                        setExpandedMenus(prev => 
                          isExpanded 
                            ? prev.filter(menu => menu !== item.title)
                            : [...prev, item.title]
                        )
                      }}
                      title={item.description}
                    >
                      <item.icon 
                        className={cn(
                          "mr-3 h-5 w-5 flex-shrink-0",
                          hasActiveChild 
                            ? "text-blue-500" 
                            : "text-gray-400 group-hover:text-gray-500"
                        )} 
                      />
                      <span className="flex-1 text-left">{item.title}</span>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    
                    {/* 子级菜单 */}
                    {isExpanded && (
                      <div className="ml-6 mt-1 space-y-1">
                        {item.children.map((child) => {
                          const isActive = location.pathname === child.href
                          
                          return (
                            <Link
                              key={child.href}
                              to={child.href}
                              className={cn(
                                "group flex items-center px-3 py-2 text-sm rounded-md transition-colors",
                                isActive
                                  ? "bg-blue-100 text-blue-700 font-medium"
                                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                              )}
                              title={child.description}
                              onClick={() => setSidebarOpen(false)}
                            >
                              {child.icon ? (
                                <child.icon className={cn(
                                  "mr-3 h-4 w-4 flex-shrink-0",
                                  isActive ? "text-blue-500" : "text-gray-400 group-hover:text-gray-500"
                                )} />
                              ) : (
                                <div className={cn(
                                  "mr-3 h-2 w-2 rounded-full flex-shrink-0",
                                  isActive ? "bg-blue-500" : "bg-gray-300"
                                )} />
                              )}
                              {child.title}
                            </Link>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              } else {
                // 一级菜单
                const isActive = location.pathname === item.href || 
                  (item.href !== '/dashboard' && location.pathname.startsWith(item.href))
                
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      "group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors",
                      isActive
                        ? "bg-blue-50 text-blue-700 border-r-2 border-blue-500"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    )}
                    title={item.description}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon 
                      className={cn(
                        "mr-3 h-5 w-5 flex-shrink-0",
                        isActive 
                          ? "text-blue-500" 
                          : "text-gray-400 group-hover:text-gray-500"
                      )} 
                    />
                    {item.title}
                  </Link>
                )
              }
            })}
          </nav>
          
          {/* 底部用户信息 */}
          <div className="border-t border-gray-200 p-4">
            <Link to="/dashboard/settings/account" className="flex items-center mb-3 group" title="账户设置">
              <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center group-hover:opacity-90">
                <User className="h-4 w-4 text-white" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900 group-hover:text-blue-700">{user?.username || user?.phone}</p>
                <p className="text-xs text-gray-500">
                  <Badge 
                    variant={certStatus === 'VERIFIED' ? 'default' : (certStatus === 'REJECTED' ? 'destructive' : 'secondary')} 
                    className="text-xs"
                  >
                    {certStatus === 'VERIFIED' && '已认证'}
                    {certStatus === 'PENDING' && '审核中'}
                    {certStatus === 'REJECTED' && '已驳回'}
                    {!certStatus && '待认证'}
                    {certStatus === 'NOT_SUBMITTED' && '待认证'}
                  </Badge>
                </p>
              </div>
            </Link>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-gray-600 hover:text-gray-900"
              onClick={() => logout()}
            >
              <LogOut className="mr-2 h-4 w-4" />
              退出登录
            </Button>
          </div>
        </div>
      </div>
      
      {/* 主内容区 */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 顶部导航栏 */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              
              {/* 面包屑导航 */}
              <div className="ml-4 lg:ml-0">
                <h1 className="text-lg font-semibold text-gray-900">
                  {(() => {
                    // 查找当前页面标题
                    for (const item of menuItems) {
                      if (item.href && (location.pathname === item.href || 
                          (item.href !== '/dashboard' && location.pathname.startsWith(item.href)))) {
                        return item.title
                      }
                      if (item.children) {
                        const activeChild = item.children.find(child => 
                          location.pathname === child.href || 
                          (child.href !== '/dashboard' && location.pathname.startsWith(child.href))
                        )
                        if (activeChild) {
                          return activeChild.title
                        }
                      }
                    }
                    return '工作台'
                  })()}
                </h1>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* 快捷操作 */}
              <Button variant="outline" size="sm" asChild>
                <Link to="/dashboard/send">
                  <Send className="h-4 w-4 mr-2" />
                  快速发送
                </Link>
              </Button>
              
              {/* 通知中心 */}
              <NotificationCenter />
              
              {/* 帮助中心 */}
              <Button variant="ghost" size="sm" asChild>
                <Link to="/help">
                  <HelpCircle className="h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </header>
        
        {/* 页面内容 */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
          {/* 认证提醒 - 只在未认证时显示 */}
          {user?.status === 'INACTIVE' && (
            <VerificationPrompt 
              userStatus={{
                isVerified: false, // Since we're already checking for INACTIVE, this will always be false
                verificationStep: 'company',
                completedSteps: []
              }}
              className="mb-6"
            />
          )}
          
          <Outlet />
        </main>
      </div>
      
      {/* 移动端遮罩 */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  )
}